import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, TextField, MenuItem } from "@mui/material";
import TodayIcon from "@mui/icons-material/Today";
import {
    BIRTHDATE_GRANULARITY,
    birthdateWireToDisplay,
    inferBirthdateGranularity,
    isBirthdateSystemToday,
    makeBirthdateSystemToday,
    resolveBirthdateGranularities,
    serializeBirthdateWire,
    MONTH_NAMES_FR,
} from "../../utils/birthdateWire";

const fsx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

/**
 * Éditeur birthdate unifié — affichage fr-FR, payload wire ISO (sans /).
 * Mode dynamique birthdate : { "$system": "today" } uniquement.
 */
const BirthdateValueEditor = ({
    value,
    onChange,
    fieldMeta,
    valuePresets,
    operator = "eq",
    accentColor = "#0369a1",
    showModeToggle = true,
}) => {
    const granularities = useMemo(() => resolveBirthdateGranularities(fieldMeta), [fieldMeta]);
    const dynamicPresets = useMemo(() => {
        const presets = Array.isArray(valuePresets) ? valuePresets : [];
        if (presets.length > 0) return presets;
        return [{ id: "today", label: "Aujourd'hui (anniversaire roulant)", value: makeBirthdateSystemToday() }];
    }, [valuePresets]);

    const isDynamic = isBirthdateSystemToday(value);
    const isArrayOp = ["in", "between"].includes(operator);

    const [mode, setMode] = useState(isDynamic ? "dynamic" : "fixed");
    const [granularity, setGranularity] = useState(() => {
        if (isDynamic) return granularities[0]?.key || BIRTHDATE_GRANULARITY.FULL;
        if (Array.isArray(value) && value[0]) return inferBirthdateGranularity(String(value[0]));
        return inferBirthdateGranularity(typeof value === "string" ? value : "");
    });
    const [display, setDisplay] = useState(() => {
        if (isDynamic || value == null || value === "") return "";
        if (Array.isArray(value)) return "";
        return birthdateWireToDisplay(granularity, String(value));
    });
    const [displayLo, setDisplayLo] = useState(() => {
        if (!Array.isArray(value)) return "";
        const gran = inferBirthdateGranularity(String(value[0] || ""));
        return birthdateWireToDisplay(gran, String(value[0] || ""));
    });
    const [displayHi, setDisplayHi] = useState(() => {
        if (!Array.isArray(value)) return "";
        const gran = inferBirthdateGranularity(String(value[0] || ""));
        return birthdateWireToDisplay(gran, String(value[1] || ""));
    });

    useEffect(() => {
        if (isBirthdateSystemToday(value)) {
            setMode("dynamic");
            return;
        }
        setMode("fixed");
        const gran = Array.isArray(value)
            ? inferBirthdateGranularity(String(value[0] || ""))
            : inferBirthdateGranularity(typeof value === "string" ? value : "");
        setGranularity(gran);
        if (Array.isArray(value)) {
            setDisplayLo(birthdateWireToDisplay(gran, String(value[0] || "")));
            setDisplayHi(birthdateWireToDisplay(gran, String(value[1] || "")));
        } else {
            setDisplay(birthdateWireToDisplay(gran, typeof value === "string" ? value : ""));
        }
    }, [value]);

    const granMeta = granularities.find((g) => g.key === granularity) || granularities[0];
    const hint = granMeta?.hint || "";

    const emitFixedSingle = (nextDisplay, nextGran = granularity) => {
        const wire = serializeBirthdateWire(nextGran, nextDisplay);
        onChange(wire);
    };

    const emitFixedBetween = (lo, hi, nextGran = granularity) => {
        onChange([
            serializeBirthdateWire(nextGran, lo),
            serializeBirthdateWire(nextGran, hi),
        ]);
    };

    const handleModeChange = (nextMode) => {
        setMode(nextMode);
        if (nextMode === "dynamic") {
            const preset = dynamicPresets[0]?.value || makeBirthdateSystemToday();
            onChange(preset);
        } else {
            onChange("");
            setDisplay("");
        }
    };

    const handleGranularityChange = (nextGran) => {
        setGranularity(nextGran);
        if (isArrayOp) {
            emitFixedBetween(displayLo, displayHi, nextGran);
        } else {
            const nextDisplay = birthdateWireToDisplay(nextGran, serializeBirthdateWire(granularity, display) || "");
            setDisplay(nextDisplay || "");
            emitFixedSingle(nextDisplay || "", nextGran);
        }
    };

    const fixedInput = (label, displayVal, onDisplayChange) => {
        if (granularity === BIRTHDATE_GRANULARITY.MONTH) {
            const monthIdx = MONTH_NAMES_FR.findIndex((n) => n.toLowerCase() === String(displayVal).toLowerCase());
            const selectVal = monthIdx >= 0 ? padMonth(monthIdx + 1) : (displayVal || "");
            return (
                <TextField select size="small" fullWidth label={label} value={selectVal} onChange={(e) => {
                    const mo = e.target.value;
                    const labelFr = MONTH_NAMES_FR[parseInt(mo, 10) - 1] || mo;
                    onDisplayChange(labelFr);
                }} sx={fsx}>
                    {MONTH_NAMES_FR.map((name, i) => (
                        <MenuItem key={name} value={padMonth(i + 1)}>{name}</MenuItem>
                    ))}
                </TextField>
            );
        }
        return (
            <TextField
                size="small"
                fullWidth
                label={label}
                placeholder={hint}
                value={displayVal}
                onChange={(e) => onDisplayChange(e.target.value)}
                helperText={hint ? `Format : ${hint}` : undefined}
                sx={fsx}
            />
        );
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            {showModeToggle && !isArrayOp && (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontSize={11} color="text.secondary">Mode :</Typography>
                    {[{ k: "fixed", l: "Fixe" }, { k: "dynamic", l: "Dynamique" }].map(({ k, l }) => (
                        <Box
                            key={k}
                            onClick={() => handleModeChange(k)}
                            sx={{
                                px: 1.5, py: 0.3, borderRadius: 99, cursor: "pointer", fontSize: 11, fontWeight: 700,
                                bgcolor: mode === k ? accentColor : "#f1f5f9",
                                color: mode === k ? "#fff" : "#64748b",
                                transition: "all .15s",
                            }}
                        >
                            {l}
                        </Box>
                    ))}
                </Box>
            )}

            {mode === "dynamic" && !isArrayOp ? (
                <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 1 }}>
                    <TodayIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                    <Typography fontSize={12} color="#15803d" fontWeight={500}>
                        {dynamicPresets.find((p) => isBirthdateSystemToday(p.value))?.label
                            || "Anniversaire roulant — comparé à aujourd'hui à chaque exécution"}
                    </Typography>
                </Box>
            ) : (
                <>
                    <TextField
                        select
                        size="small"
                        label="Précision"
                        value={granularity}
                        onChange={(e) => handleGranularityChange(e.target.value)}
                        sx={{ ...fsx, maxWidth: 320 }}
                    >
                        {granularities.map((g) => (
                            <MenuItem key={g.key} value={g.key}>{g.label}</MenuItem>
                        ))}
                    </TextField>

                    {isArrayOp && operator === "between" ? (
                        <Box display="flex" gap={1}>
                            {fixedInput("De", displayLo, (v) => { setDisplayLo(v); emitFixedBetween(v, displayHi); })}
                            {fixedInput("À", displayHi, (v) => { setDisplayHi(v); emitFixedBetween(displayLo, v); })}
                        </Box>
                    ) : isArrayOp ? (
                        <TextField
                            size="small"
                            fullWidth
                            label="Valeurs (séparées par virgule)"
                            value={[displayLo, displayHi].filter(Boolean).join(", ") || (Array.isArray(value) ? value.map((w) => birthdateWireToDisplay(granularity, String(w))).join(", ") : "")}
                            onChange={(e) => {
                                const parts = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                                onChange(parts.map((p) => serializeBirthdateWire(granularity, p)));
                            }}
                            helperText={`Format ${hint} — envoyé en wire ISO (sans /)`}
                            sx={fsx}
                        />
                    ) : (
                        fixedInput("Valeur", display, (v) => { setDisplay(v); emitFixedSingle(v); })
                    )}
                </>
            )}
        </Box>
    );
};

function padMonth(n) {
    return String(n).padStart(2, "0");
}

export default BirthdateValueEditor;
